import React from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Switch,
  Textarea
} from '@heroui/react';
import { RiAddLine, RiDeleteBinLine } from '@remixicon/react';

import type { PlanPayload } from '@/types';

interface PlanFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  formData: PlanPayload;
  setFormData: React.Dispatch<React.SetStateAction<PlanPayload>>;
  isSubmitting: boolean;
  onSubmit: () => Promise<void> | void;
  isEditMode: boolean;
}

export function PlanFormModal({
  isOpen,
  onOpenChange,
  formData,
  setFormData,
  isSubmitting,
  onSubmit,
  isEditMode
}: PlanFormModalProps) {
  const [error, setError] = React.useState<string | null>(null);

  const handleClose = React.useCallback(() => {
    onOpenChange(false);
    setError(null);
  }, [onOpenChange]);

  const handleSubmit = React.useCallback(async () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.slug.trim()) {
      setError('Slug is required');
      return;
    }
    setError(null);
    await onSubmit();
  }, [formData.name, formData.slug, onSubmit]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleNameBlur = React.useCallback(() => {
    if (!formData.slug.trim() && formData.name.trim()) {
      const slug = formData.name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name, formData.slug, setFormData]);

  const addFeature = React.useCallback(() => {
    setFormData((prev) => ({ ...prev, features: [...prev.features, ''] }));
  }, [setFormData]);

  const removeFeature = React.useCallback(
    (index: number) => {
      setFormData((prev) => ({
        ...prev,
        features: prev.features.filter((_, i) => i !== index)
      }));
    },
    [setFormData]
  );

  const updateFeature = React.useCallback(
    (index: number, value: string) => {
      setFormData((prev) => {
        const next = [...prev.features];
        next[index] = value;
        return { ...prev, features: next };
      });
    },
    [setFormData]
  );

  const features = Array.isArray(formData.features) ? formData.features : [];

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="lg" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              {isEditMode ? 'Edit Plan' : 'Create Plan'}
            </ModalHeader>
            <ModalBody onKeyDown={handleKeyDown}>
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    label="Name"
                    placeholder="e.g. Pro Plan"
                    value={formData.name}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, name: value }))}
                    onBlur={handleNameBlur}
                    isRequired
                    isInvalid={Boolean(error)}
                    errorMessage={error ?? undefined}
                  />
                  <Input
                    label="Slug"
                    placeholder="e.g. pro-plan"
                    value={formData.slug}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, slug: value }))}
                    isRequired
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Input
                    type="number"
                    label="Price"
                    placeholder="0"
                    min={0}
                    step="any"
                    value={String(formData.price ?? '')}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, price: value || 0 }))}
                  />
                  <Input
                    label="Currency"
                    placeholder="USD"
                    maxLength={3}
                    value={formData.currency ?? ''}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, currency: value || 'USD' }))}
                  />
                  <Input
                    type="number"
                    label="Monthly Event Limit"
                    placeholder="e.g. 10000"
                    min={1}
                    value={String(formData.credit_amount ?? '')}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, credit_amount: parseInt(value, 10) || 1 }))
                    }
                    description="Processing capacity per month (events). Defines plan limit, not a monetary balance."
                    isRequired
                  />
                </div>

                <Textarea
                  label="Description"
                  placeholder="Optional description"
                  minRows={2}
                  value={formData.description ?? ''}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, description: value ?? '' }))}
                />

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Input
                    type="number"
                    label="Sort order"
                    placeholder="0"
                    min={0}
                    value={String(formData.sort_order ?? '')}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, sort_order: parseInt(value, 10) || 0 }))
                    }
                  />
                  <div className="flex items-end pb-2">
                    <Switch
                      isSelected={formData.is_active ?? true}
                      onValueChange={(value) => setFormData((prev) => ({ ...prev, is_active: value }))}
                    >
                      Active
                    </Switch>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-small text-foreground-500">Features</span>
                    <Button size="sm" variant="flat" startContent={<RiAddLine className="h-4 w-4" />} onPress={addFeature}>
                      Add feature
                    </Button>
                  </div>
                  {features.length === 0 ? (
                    <p className="text-default-400 text-sm">No features. Click &quot;Add feature&quot; to add one.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {features.map((feature, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            placeholder={`Feature ${index + 1}`}
                            value={feature}
                            onValueChange={(value) => updateFeature(index, value)}
                            className="flex-1"
                          />
                          <Button
                            isIconOnly
                            size="md"
                            variant="light"
                            color="danger"
                            onPress={() => removeFeature(index)}
                            aria-label="Remove feature"
                          >
                            <RiDeleteBinLine className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSubmitting} onPress={handleSubmit}>
                {isEditMode ? 'Update Plan' : 'Create Plan'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
